import { useState, useCallback, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Check, Loader2, XCircle, ShieldAlert, Plus, X } from "lucide-react";
import { extractInstagramUsername, validateInstagramInput, isValidInstagramUsername } from "@/utils/instagramUsername";
import { useAuthImage, preloadAuthImage } from "@/hooks/useAuthImage";
import { formatFollowerCount } from "@/utils/instagram";
import { supabase } from "@/integrations/supabase/client";
import { logEdgeFunctionError } from "@/utils/sentry";

const MAX_PROFILES = 5;

export interface VerificationResult {
  status: 'idle' | 'loading' | 'success' | 'error';
  username?: string;
  full_name?: string;
  profile_pic_url?: string;
  followers_count?: number;
  is_private?: boolean;
  is_blocked?: boolean;
  errorMessage?: string;
}

interface ProfileAvatarProps {
  url?: string;
  fullName?: string;
  size?: number;
}

export const ProfileAvatar = ({ url, fullName, size = 32 }: ProfileAvatarProps) => {
  const [error, setError] = useState(false);
  const blobUrl = useAuthImage(url);

  if (!blobUrl || error) {
    return (
      <div
        className="rounded-full bg-gradient-to-br from-[#F9B896] to-[#FCCAB0] flex items-center justify-center text-white font-bold flex-shrink-0"
        style={{ width: size, height: size, fontSize: size * 0.4 }}
      >
        {fullName?.charAt(0)?.toUpperCase() || "?"}
      </div>
    );
  }

  return (
    <img
      src={blobUrl}
      alt={fullName || "avatar"}
      referrerPolicy="no-referrer"
      onError={() => setError(true)}
      className="rounded-full object-cover flex-shrink-0 border border-gray-200/60"
      style={{ width: size, height: size }}
    />
  );
};

type InvokeErrorInfo = {
  status: number | null;
  isNetworkError: boolean;
  isServerError: boolean;
  isRateLimit: boolean;
  serverMessage: string | null;
};

const parseInvokeError = async (error: unknown): Promise<InvokeErrorInfo> => {
  const rawMessage =
    error instanceof Error
      ? error.message
      : typeof error === 'object' && error !== null && 'message' in error
        ? String((error as { message: unknown }).message ?? '')
        : '';
  const lower = rawMessage.toLowerCase();
  // Cross-browser fetch failure signatures from supabase-js (FunctionsFetchError).
  const isNetworkError =
    lower.includes('failed to send') ||
    lower.includes('failed to fetch') ||
    lower === 'load failed' ||
    lower.includes('networkerror');

  let status: number | null = null;
  let serverMessage: string | null = null;
  // FunctionsHttpError stashes the Response on .context — read it for the real status/body.
  const ctx = (error as { context?: { status?: number; clone?: () => Response } }).context;
  if (ctx && typeof ctx.status === 'number') {
    status = ctx.status;
    if (typeof ctx.clone === 'function') {
      try {
        const body = await ctx.clone().json();
        if (body && typeof body === 'object' && 'error' in body) {
          const m = (body as { error: unknown }).error;
          if (typeof m === 'string') serverMessage = m;
        }
      } catch {
        // body not JSON or already consumed — fine, keep serverMessage null
      }
    }
  }

  return {
    status,
    serverMessage,
    isNetworkError,
    isServerError: status !== null && status >= 500,
    isRateLimit: status === 429,
  };
};

/**
 * Verifies Instagram profiles via the `verify-profile` Edge Function and keeps
 * the per-input results in state. When `storageKey` is provided, only the
 * `success` results are mirrored to localStorage so that navigating back to
 * an earlier onboarding step doesn't force the user to click "Sprawdź" again
 * for profiles they've already verified. Loading and error states are
 * intentionally not persisted — those should always re-resolve on the live UI.
 */
export const useProfileVerification = (
  userId: string | null,
  storageKey?: string,
  onVerifySuccess?: (index: number, verifiedUsername: string) => void,
) => {
  // Keep the latest callback in a ref so verifyProfile's `useCallback` deps
  // don't churn on every parent render — otherwise typing in the parent
  // would re-create verifyProfile and tear down its `loading` debounce.
  const onVerifySuccessRef = useRef(onVerifySuccess);
  useEffect(() => {
    onVerifySuccessRef.current = onVerifySuccess;
  }, [onVerifySuccess]);

  const [verifications, setVerifications] = useState<Record<number, VerificationResult>>(() => {
    if (!storageKey || typeof window === "undefined") return {};
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object") return {};
      const restored: Record<number, VerificationResult> = {};
      for (const [key, val] of Object.entries(parsed)) {
        const idx = Number(key);
        const v = val as VerificationResult | undefined;
        if (Number.isFinite(idx) && v && v.status === "success") {
          restored[idx] = v;
        }
      }
      return restored;
    } catch {
      return {};
    }
  });

  // Persist successful verifications. Skip the very first render so that
  // restored state doesn't trigger a redundant write.
  const skipNextPersist = useRef(true);
  useEffect(() => {
    if (!storageKey) return;
    if (skipNextPersist.current) {
      skipNextPersist.current = false;
      return;
    }
    const successOnly: Record<number, VerificationResult> = {};
    for (const [key, val] of Object.entries(verifications)) {
      if (val.status === "success") successOnly[Number(key)] = val;
    }
    if (Object.keys(successOnly).length === 0) {
      window.localStorage.removeItem(storageKey);
    } else {
      window.localStorage.setItem(storageKey, JSON.stringify(successOnly));
    }
  }, [verifications, storageKey]);

  const verifyProfile = useCallback(async (index: number, input: string) => {
    if (!userId) {
      setVerifications(prev => ({
        ...prev,
        [index]: { status: 'error', errorMessage: "Zaloguj się, żebyśmy mogli sprawdzić profil." },
      }));
      return;
    }

    const username = extractInstagramUsername(input);
    if (!username || !isValidInstagramUsername(username)) return;

    setVerifications(prev => ({ ...prev, [index]: { status: 'loading' } }));

    const MAX_RETRIES = 2;
    const RETRY_DELAYS = [1000, 2000];
    let lastError: Error | null = null;
    let lastErrorInfo: InvokeErrorInfo | null = null;
    let data: any = null;

    try {
      for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        const result = await supabase.functions.invoke('verify-profile', {
          body: {
            user_id: userId,
            instagram_username: username
          }
        });

        if (result.error) {
          lastError = new Error(result.error.message);
          lastErrorInfo = await parseInvokeError(result.error);
          // Retry transient failures (network blip or 5xx/timeout). Don't retry
          // 4xx — those are deterministic (bad input, rate limit, auth).
          const isRetryable = lastErrorInfo.isNetworkError || lastErrorInfo.isServerError;
          if (isRetryable && attempt < MAX_RETRIES) {
            await new Promise(r => setTimeout(r, RETRY_DELAYS[attempt]));
            continue;
          }
          throw lastError;
        }

        data = result.data;
        break;
      }

      if (!data.success) {
        if (data.is_potentially_adult) {
          setVerifications(prev => ({
            ...prev,
            [index]: { status: 'error', is_blocked: true, errorMessage: "Niestety nie możemy dodać tego profilu." },
          }));
          return;
        }
        setVerifications(prev => ({
          ...prev,
          [index]: { status: 'error', errorMessage: data.message || "Nie znaleźliśmy tego profilu — sprawdź pisownię i spróbuj jeszcze raz." },
        }));
        return;
      }

      const verified: VerificationResult = {
        status: 'success',
        username: data.username,
        full_name: data.full_name,
        profile_pic_url: data.profile_pic_url,
        followers_count: data.followers_count,
        is_private: data.is_private || false,
      };

      // Preload avatar before showing success to avoid image pop-in
      if (data.profile_pic_url) {
        const preloaded = await preloadAuthImage(data.profile_pic_url);
        if (preloaded) URL.revokeObjectURL(preloaded);
      }

      setVerifications(prev => ({ ...prev, [index]: verified }));
      onVerifySuccessRef.current?.(index, data.username);
    } catch (err) {
      const info = lastErrorInfo ?? (await parseInvokeError(err));
      logEdgeFunctionError('verify-profile', err, {
        username,
        index,
        isNetworkError: info.isNetworkError,
        status: info.status,
        serverMessage: info.serverMessage,
      });
      let message: string;
      if (info.isRateLimit) {
        message = 'Sprawdzamy zbyt wiele profili naraz. Daj nam chwilę i spróbuj ponownie.';
      } else if (info.isNetworkError) {
        message = 'Coś się dzieje z połączeniem. Sprawdź internet i spróbuj jeszcze raz.';
      } else if (info.isServerError) {
        message = 'U nas chwilowo coś nie działa. Spróbuj jeszcze raz za moment.';
      } else {
        message = 'Nie udało nam się sprawdzić tego profilu — spróbujmy jeszcze raz.';
      }
      setVerifications(prev => ({
        ...prev,
        [index]: { status: 'error', errorMessage: message },
      }));
    }
  }, [userId]);

  const clearVerification = useCallback((index: number) => {
    setVerifications(prev => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  }, []);

  const removeAndReindex = useCallback((removedIndex: number) => {
    setVerifications(prev => {
      const next: Record<number, VerificationResult> = {};
      for (const [key, val] of Object.entries(prev)) {
        const idx = Number(key);
        if (idx < removedIndex) next[idx] = val;
        else if (idx > removedIndex) next[idx - 1] = val;
      }
      return next;
    });
  }, []);

  const hasUnverifiedInputs = (inputs: string[]): boolean => {
    return inputs.some((val, idx) => {
      if (val.trim() === "") return false;
      return verifications[idx]?.status !== 'success';
    });
  };

  return { verifications, verifyProfile, clearVerification, removeAndReindex, hasUnverifiedInputs };
};

interface VerifiableProfileInputProps {
  index: number;
  value: string;
  onChange: (index: number, value: string) => void;
  onRemove: (index: number) => void;
  onVerify: (index: number, input: string) => void;
  onEnterAdd: () => void;
  verification?: VerificationResult;
  validationError?: string;
  canRemove: boolean;
  icon: React.ReactNode;
  totalCount: number;
}

export const VerifiableProfileInput = ({
  index,
  value,
  onChange,
  onRemove,
  onVerify,
  onEnterAdd,
  verification,
  validationError,
  canRemove,
  icon,
  totalCount,
}: VerifiableProfileInputProps) => {
  const isVerified = verification?.status === 'success' && !verification.is_private && !verification.is_blocked;
  const isVerifying = verification?.status === 'loading';
  const hasVerifyError = verification?.status === 'error';
  const canVerify = value.trim() !== "" && !validationError && !isVerifying && verification?.status !== 'success';

  return (
    <div className="space-y-1">
      <div className="flex gap-2 items-center">
        {/* Avatar slot - always reserved for alignment */}
        <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center">
          {isVerified && verification.profile_pic_url ? (
            <ProfileAvatar
              url={verification.profile_pic_url}
              fullName={verification.full_name}
              size={32}
            />
          ) : hasVerifyError ? (
            <XCircle className="w-6 h-6 text-destructive" />
          ) : null}
        </div>

        <div className="flex-1">
          <div className="relative">
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400">
              {icon}
            </div>
            <Input
              type="text"
              placeholder="nazwa_profilu lub link"
              value={value}
              onChange={e => onChange(index, e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && value.trim() !== "" && !validationError) {
                  e.preventDefault();
                  if (canVerify) {
                    onVerify(index, value);
                  } else if (totalCount < MAX_PROFILES) {
                    onEnterAdd();
                  }
                }
              }}
              className={`pl-10 h-12 text-base border-2 rounded-xl transition-all ${
                hasVerifyError || verification?.is_blocked
                  ? "border-destructive/50 bg-destructive/5"
                  : validationError
                  ? "border-destructive/50 bg-destructive/5"
                  : isVerified
                  ? "border-primary bg-primary/5"
                  : "border-gray-200 focus:border-primary"
              }`}
            />
            {isVerified && !validationError && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <div className="w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                  <Check className="w-3 h-3 text-primary-foreground" />
                </div>
              </div>
            )}
          </div>
        </div>

        {canVerify && (
          <Button
            size="sm"
            onClick={() => onVerify(index, value)}
            className="h-12 px-4 rounded-xl font-medium animate-subtle-breathe hover:[animation-play-state:paused]"
          >
            Sprawdź
          </Button>
        )}

        {isVerifying && (
          <Button size="sm" disabled className="h-12 px-4 rounded-xl font-medium">
            <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
            <span className="text-sm">Sprawdzam</span>
          </Button>
        )}

        {canRemove && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onRemove(index)}
            className="h-12 w-12 rounded-xl text-gray-400 hover:text-destructive flex-shrink-0"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {isVerified && (
        <p className="text-xs text-gray-500 ml-1 pl-10">
          {verification.full_name} · Obserwujących: {formatFollowerCount(verification.followers_count ?? 0)}
        </p>
      )}

      {verification?.is_private && verification.status === 'success' && (
        <p className="text-xs text-amber-600 ml-1 pl-10 flex items-center gap-1">
          <ShieldAlert className="w-3 h-3" />
          Profil prywatny — nie będziemy mogli analizować treści
        </p>
      )}

      {hasVerifyError && (
        <p className="text-xs text-destructive ml-1 pl-10">
          {verification.errorMessage}
        </p>
      )}

      {validationError && <p className="text-xs text-destructive ml-1 pl-10">{validationError}</p>}
    </div>
  );
};
